import { Box } from '@mui/material';
import { DataGridProProps, GridCellParams, GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { AgentReport } from '../../../@types/api';
import { appendTotalsRow, getTotalsRowClass } from '../../../helpers';
import { createCommissionColumn, createNumberColumn } from '../../../helpers/columns';
import { defaultColumnsAgentReports } from '../../../helpers/table';
import { usePagination } from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import { createColumn } from '../../../utils';
import DateRangeSelect, { DayjsTimestamp } from '../../molecules/DateRangeSelect';
import AgentReportsDetails from '../../organisms/AgentReportsDetails';
import TableTemplate from '../../templates/TableTemplate';
import { dayjs } from '../../../config/dayjs';
import { partnerReportsStyles } from './partnersReportsTableConfig';
import API_ROUTES from '../../../constants/apiRoutes';

const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'Total',
    description: 'Total',
    headerClassName: 'customGroupHeader',
    children: [
      { field: 'totalStake' },
      { field: 'totalWinnings' },
      { field: 'jackpotAmount' },
      { field: 'totalProfit' },
      { field: 'totalCommission' },
      { field: 'totalProfitAfterCommission' },
    ],
  },
  {
    groupId: 'Tickets',
    description: 'Tickets',
    headerClassName: 'customGroupHeader',
    children: [{ field: 'ticketsTotal' }, { field: 'ticketsLost' }, { field: 'ticketsWon' }, { field: 'ticketsOpen' }],
  },
];

const generateTotalsRow = (data?: AgentReport) => {
  if (data) {
    return {
      ...data,
      userId: 'totals',
      fullName: 'Totals',
      id: 'totals',
      isTotalsRow: true,
    };
  }
};

const getMobileCardHeaderItems = (report: AgentReport) => [
  { value: report.fullName, flex: 1, align: 'left' as const },
  { label: 'Profit', value: report.totalProfit.toFixed(2) },
  {
    label: 'After Comm.',
    value: report.totalProfitAfterCommission.toFixed(2),
  },
];

const AgentsReportPage = () => {
  const [selectedRow, setSelectedRow] = useState<AgentReport | null>(null);

  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<AgentReport>(
    API_ROUTES.REPORTS.PARTNERS.AGENTS,
    {
      page: 1,
      limit: 25,
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    }
  );

  const totalsRow = generateTotalsRow(data?.totals);
  const aggregatedData = appendTotalsRow(data?.items, totalsRow);

  const onCellClick = (params: GridCellParams<AgentReport>) => {
    setSelectedRow(params.row);
  };

  const columns: GridColDef[] = [
    createColumn('username', 'Username', { minWidth: 150, alwaysVisible: true }),
    createColumn('fullName', 'Name', { minWidth: 150 }),
    createCommissionColumn(onCellClick),
    createColumn('playersCount', 'Players'),
    createNumberColumn('balance', 'Balance'),
    createNumberColumn('totalStake', 'Stake'),
    createNumberColumn('totalWinnings', 'Winnings'),
    createNumberColumn('jackpotAmount', 'Jackpots'),
    createNumberColumn('totalProfit', 'Profit', { alwaysVisible: true }),
    createNumberColumn('totalCommission', 'Commision', { alwaysVisible: true }),
    createNumberColumn('totalProfitAfterCommission', 'Profit After Comm.', { alwaysVisible: true }),
    createColumn('ticketsTotal', 'Total'),
    createColumn('ticketsLost', 'Lost'),
    createColumn('ticketsWon', 'Won'),
    createColumn('ticketsOpen', 'Open'),
  ];

  const { handleSort } = useSort(changeQuery);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  const onCloseModal = () => {
    setSelectedRow(null);
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  return (
    <Box sx={partnerReportsStyles.container}>
      <TableTemplate
        getRowId={(params) => params.id}
        columnGroupingModel={columnGroupingModel}
        rows={aggregatedData || []}
        columns={columns}
        sx={partnerReportsStyles.table as DataGridProProps['sx']}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        getRowClassName={getTotalsRowClass}
        changeQuery={changeQuery}
        defaultVisibleColumns={defaultColumnsAgentReports}
        mobileCardHeaderItems={getMobileCardHeaderItems}
        showExport
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
      />
      {selectedRow && selectedRow.commission.info && (
        <AgentReportsDetails
          closeModal={onCloseModal}
          dateRangeFilter={date}
          {...selectedRow}
          commission={selectedRow.commission.info}
        />
      )}
    </Box>
  );
};

export default AgentsReportPage;
